import React, { useState, useEffect, useRef } from 'react';
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, Image, Modal } from 'react-native';
import { TextInput, Button, Title, Text, Snackbar } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useAuth } from '../../context/AuthContext';
import { isValidEmail, isValidPassword } from '../../utils';
import AsyncStorage from '@react-native-async-storage/async-storage';

const LoginScreen = ({ navigation, route }) => {
  // Message from RegisterScreen
  const [message, setMessage] = useState(route.params?.message || '');
  
  // Form state with persistent values using AsyncStorage
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  
  // Input refs for direct access
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  
  // Error handling
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  // Snackbar
  const [snackbarVisible, setSnackbarVisible] = useState(!!message); // Mesaj varsa snackbar'ı göster
  const [snackbarMessage, setSnackbarMessage] = useState(message || '');
  
  // Error Modal
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  
  // Persist form state for web platform
  const [formState, setFormState] = useState({
    email: '',
    password: ''
  });
  
  // Auth context
  const { signIn } = useAuth();
  
  // Form değerlerini yerel depolamadan yükle
  useEffect(() => {
    const loadSavedValues = async () => {
      try {
        const savedEmail = await AsyncStorage.getItem('tempLoginEmail');
        const savedFormState = await AsyncStorage.getItem('tempLoginFormState');
        
        if (savedEmail) {
          setEmail(savedEmail);
        }
        
        if (savedFormState) {
          const parsedState = JSON.parse(savedFormState);
          setFormState(parsedState);
          
          // Form alanlarını kaydettigimiz verilerle doldur
          if (parsedState.email) setEmail(parsedState.email);
          if (parsedState.password) setPassword(parsedState.password);
        }
      } catch (error) {
        console.error('Form değerleri yüklenirken hata:', error);
      }
    };
    
    loadSavedValues();
  }, []);
  
  // Registration message handling
  useEffect(() => {
    if (route.params?.message) {
      setSnackbarMessage(route.params.message);
      setSnackbarVisible(true);
      
      // Parametre temizliği - ekran her açıldığında tekrar gösterilmemesi için
      navigation.setParams({ message: undefined });
    }
  }, [route.params?.message, navigation]);

  // Form validation
  const validateForm = () => {
    let isValid = true;
    
    // Email validation
    if (!email) {
      setEmailError('Email adresi gereklidir');
      isValid = false;
    } else if (!isValidEmail(email)) {
      setEmailError('Geçerli bir email adresi giriniz');
      isValid = false;
    } else {
      setEmailError('');
    }
    
    // Password validation
    if (!password) {
      setPasswordError('Şifre gereklidir');
      isValid = false;
    } else if (!isValidPassword(password)) {
      setPasswordError('Şifre en az 6 karakter olmalıdır ve bir özel karakter ile birer küçük, büyük harf içermelidir.');
      isValid = false;
    } else {
      setPasswordError('');
    }
    
    // Form doğru değilse ve web platformundaysak DOM modalını göster
    if (!isValid && typeof document !== 'undefined') {
      // DOM ile modal hata gösterim
      const errorDiv = document.createElement('div');
      errorDiv.style.position = 'fixed';
      errorDiv.style.top = '50%';
      errorDiv.style.left = '50%';
      errorDiv.style.transform = 'translate(-50%, -50%)';
      errorDiv.style.backgroundColor = 'white';
      errorDiv.style.padding = '20px';
      errorDiv.style.borderRadius = '10px';
      errorDiv.style.boxShadow = '0 0 10px rgba(0,0,0,0.3)';
      errorDiv.style.zIndex = '9999';
      errorDiv.style.maxWidth = '80%';
      errorDiv.style.textAlign = 'center';
      
      const errorTitle = document.createElement('h3');
      errorTitle.innerText = 'Form Hataları';
      errorTitle.style.marginBottom = '10px';
      errorTitle.style.color = '#d32f2f';
      
      const errorText = document.createElement('p');
      errorText.innerText = 'Lütfen aşağıdaki hataları düzeltin:';
      errorText.style.marginBottom = '15px';
      
      // Hata listesi
      const errorList = document.createElement('ul');
      errorList.style.textAlign = 'left';
      errorList.style.marginBottom = '15px';
      
      if (emailError) {
        const listItem = document.createElement('li');
        listItem.innerText = `Email: ${emailError}`;
        listItem.style.marginBottom = '5px';
        listItem.style.color = '#d32f2f';
        errorList.appendChild(listItem);
      }
      
      if (passwordError) {
        const listItem = document.createElement('li');
        listItem.innerText = `Şifre: ${passwordError}`;
        listItem.style.marginBottom = '5px';
        listItem.style.color = '#d32f2f';
        errorList.appendChild(listItem);
      }
      
      errorDiv.appendChild(errorTitle);
      errorDiv.appendChild(errorText);
      errorDiv.appendChild(errorList);
      
      const closeButton = document.createElement('button');
      closeButton.innerText = 'Kapat';
      closeButton.style.backgroundColor = '#f44336';
      closeButton.style.color = 'white';
      closeButton.style.border = 'none';
      closeButton.style.padding = '8px 16px';
      closeButton.style.borderRadius = '4px';
      closeButton.style.cursor = 'pointer';
      closeButton.onclick = function() {
        document.body.removeChild(errorDiv);
      };
      
      errorDiv.appendChild(closeButton);
      
      // DOM'a ekle
      document.body.appendChild(errorDiv);
    }
    
    return isValid;
  };
  
  // Form değerlerini her değişimde sakla
  const saveFormValues = async (emailValue, passwordValue) => {
    try {
      const formValues = {
        email: emailValue || email,
        password: passwordValue || password
      };
      
      // Form değerlerini yerel depolamaya kaydet
      await AsyncStorage.setItem('tempLoginEmail', formValues.email);
      await AsyncStorage.setItem('tempLoginFormState', JSON.stringify(formValues));
      
      // State'i güncelle
      setFormState(formValues);
    } catch (err) {
      console.error('Form değerleri kaydedilirken hata:', err);
    }
  };

  // Modal kapatma fonksiyonu
  const closeErrorModal = () => {
    setErrorModalVisible(false);
  };
  
  // Email değiştiğinde
  const handleEmailChange = (value) => {
    setEmail(value);
    saveFormValues(value, password);
  };
  
  // Password değiştiğinde
  const handlePasswordChange = (value) => {
    setPassword(value);
    saveFormValues(email, value);
  };
  
  // Login handler
  const handleLogin = async (e) => {
    // Web'de form gönderimini engelle
    if (e && e.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Form bilgilerini kaydet (web'de kaybolmaması için)
    await saveFormValues(email, password);
    
    if (validateForm()) {
      setIsLoading(true);
      
      try {
        // signIn artık hata fırlatmıyor, bir yanıt döndürüyor
        const response = await signIn(email, password);
        
        // Başarısız giriş kontrolü - success false ise hata olarak işle
        if (response && response.success === false) {
          //console.log('Login failed with response:', response);
          
          // Hata mesajını error değişkenine yükle ve hata işleme kodunu tetikle
          const error = {
            message: response.message || 'Giriş başarısız oldu.',
            errors: response.errors || {}
          };
          
          // Başarısız giriş durumunda Register ekranına otomatik yönlendirme olmaması için
          // Burada return ekliyoruz, böylece catch bloğuna yönlendirir
          throw error; // yerel catch blogumuza gönder
        }
        
        // Login başarılı - form bilgilerini temizle
        await AsyncStorage.removeItem('tempLoginEmail');
        await AsyncStorage.removeItem('tempLoginFormState');
      } catch (error) {
        //console.log('LoginScreen - handleLogin error:', error);
        
        // Hata mesajını belirle
        let errorMessage = error.message || 'Giriş başarısız. Lütfen bilgilerinizi kontrol ediniz.';
        let errorFields = [];
        
        // API errors
        if (error.errors) {
          for (const [field, message] of Object.entries(error.errors)) {
            if (field === 'username' || field === 'email') {
              setEmailError(message);
              errorFields.push(`Email: ${message}`);
            }
            if (field === 'password') {
              setPasswordError(message);
              errorFields.push(`Şifre: ${message}`);
            }
          }
        }
        
        // DOM ile modal hata gösterim - web platformu için
        const errorDiv = document.createElement('div');
        errorDiv.style.position = 'fixed';
        errorDiv.style.top = '50%';
        errorDiv.style.left = '50%';
        errorDiv.style.transform = 'translate(-50%, -50%)';
        errorDiv.style.backgroundColor = 'white';
        errorDiv.style.padding = '20px';
        errorDiv.style.borderRadius = '10px';
        errorDiv.style.boxShadow = '0 0 10px rgba(0,0,0,0.3)';
        errorDiv.style.zIndex = '9999';
        errorDiv.style.maxWidth = '80%';
        errorDiv.style.textAlign = 'center';
        
        const errorTitle = document.createElement('h3');
        errorTitle.innerText = 'Giriş Başarısız';
        errorTitle.style.marginBottom = '10px';
        errorTitle.style.color = '#d32f2f';
        
        const errorText = document.createElement('p');
        errorText.innerText = errorMessage;
        errorText.style.marginBottom = '15px';
        
        // Hata alanları varsa ayrı liste olarak göster
        if (errorFields.length > 0) {
          const errorList = document.createElement('ul');
          errorList.style.textAlign = 'left';
          errorList.style.marginBottom = '15px';
          
          errorFields.forEach(field => {
            const listItem = document.createElement('li');
            listItem.innerText = field;
            listItem.style.marginBottom = '5px';
            listItem.style.color = '#d32f2f';
            errorList.appendChild(listItem);
          });
          
          errorDiv.appendChild(errorTitle);
          errorDiv.appendChild(errorText);
          errorDiv.appendChild(errorList);
        } else {
          errorDiv.appendChild(errorTitle);
          errorDiv.appendChild(errorText);
        }
        
        const closeButton = document.createElement('button');
        closeButton.innerText = 'Kapat';
        closeButton.style.backgroundColor = '#f44336';
        closeButton.style.color = 'white';
        closeButton.style.border = 'none';
        closeButton.style.padding = '8px 16px';
        closeButton.style.borderRadius = '4px';
        closeButton.style.cursor = 'pointer';
        closeButton.onclick = function() {
          document.body.removeChild(errorDiv);
        };
        
        errorDiv.appendChild(closeButton);
        
        // DOM'a ekle
        document.body.appendChild(errorDiv);
        
        // Aynı zamanda snackbar için de mesaj ayarla (yedekleme mekanizması)
        setSnackbarMessage(errorMessage);
        setSnackbarVisible(true);
        
        // Hata durumunda form bilgilerini tekrar form alanlarına yerleştir
        if (emailInputRef.current) {
          emailInputRef.current.setNativeProps({ text: email });
        }
        if (passwordInputRef.current) {
          passwordInputRef.current.setNativeProps({ text: password });
        }
        
        // Form bilgilerini kaydet (web'de yeniden kaybolmaması için)
        await saveFormValues(email, password);
      } finally {
        setIsLoading(false);
      }
    }
    
    // Form işlemi sonrası varsayılan davranışı engelle
    return false;
  };
  
  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <ScrollView 
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.logoContainer}>
          <Image source={require('../../../assets/back.jpeg')} style={styles.logo} />
        </View>
        <View style={{alignItems: 'center', marginBottom: 20}}>
          <Title style={styles.title}>İlk'Ay Kids</Title>
          <Text style={styles.subtitle}>Hoş Geldiniz</Text>
        </View>
        
        <View style={styles.formContainer}>
          <TextInput
            ref={emailInputRef}
            label="Email"
            value={email}
            onChangeText={handleEmailChange}
            mode="outlined"
            keyboardType="email-address"
            autoCapitalize="none"
            error={!!emailError}
            style={styles.input}
            left={<TextInput.Icon icon="email" color="rgb(125, 76, 236)" />}
            theme={{ 
              colors: { 
                primary: 'rgb(125, 76, 236)', 
                underlineColor: 'transparent',
                background: 'white',
                text: '#000',
                placeholder: '#555'
              } 
            }}
            outlineColor="#ccc"
            placeholderTextColor="#555"
            defaultValue={formState.email}
          />
          {emailError ? <Text style={styles.errorText}>{emailError}</Text> : null}
          
          <TextInput
            ref={passwordInputRef}
            value={password}
            label="Şifre"

            onChangeText={handlePasswordChange}
            secureTextEntry={!passwordVisible}
            mode="outlined"
            error={!!passwordError}
            style={styles.input}
            left={<TextInput.Icon icon="lock" color="rgb(125, 76, 236)" />}
            right={
              <TextInput.Icon 
                icon={passwordVisible ? "eye-off" : "eye"} 
                color="rgb(125, 76, 236)"
                onPress={() => setPasswordVisible(!passwordVisible)} 
              />
            }
            theme={{ 
              colors: { 
                primary: 'rgb(125, 76, 236)', 
                underlineColor: 'transparent',
                background: 'white',
                text: '#000',
                placeholder: '#555'
              } 
            }}
            outlineColor="#ccc"
            placeholderTextColor="#555"
            defaultValue={formState.password}
          />
          {passwordError ? <Text style={styles.errorText}>{passwordError}</Text> : null}
          <View style={styles.registerContainer}>
            <Text style={styles.registerText}>Hesabınız yok mu?</Text>
            <Button 
              mode="text" 
              onPress={() => navigation.navigate('Register')}
              disabled={isLoading}
              color="rgb(125, 76, 236)"
              fontWeight="bold"
              labelStyle={{color: '#fff'}}
            >
              Üye Ol
            </Button>
          </View>
          <Button
            mode="contained"
            onPress={(e) => handleLogin(e)}
            style={styles.button}
            labelStyle={styles.buttonText}
            loading={isLoading}
            disabled={isLoading}
            color="rgb(125, 76, 236)"
          >
            Giriş Yap
          </Button>
          
          {/* Menüyü görüntülemek için buton */}
          <View style={styles.menuButtonContainer}>
            <Button
              mode="outlined"
              onPress={() => navigation.navigate('MenuMain')}
              color="rgb(125, 76, 236)"
              style={styles.menuButton}
              labelStyle={{color: '#fff'}}
            >
              Menüyü Görüntüle
            </Button>
          </View>
        </View>
      </ScrollView>
      
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={5000}
        style={{
          backgroundColor: snackbarMessage.includes('başarı') ? '#4CAF50' : '#3498db',
        }}
        action={{
          label: 'Tamam',
          onPress: () => setSnackbarVisible(false),
          color: 'white'
        }}
      >
        {snackbarMessage}
      </Snackbar>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  scrollContent: {
    flexGrow: 1,
    paddingTop: 0,
    paddingBottom: 20,
    paddingHorizontal: 0,
    justifyContent: 'flex-start',
  },
  logoContainer: {
    width: '100%',
    marginBottom: 20,
    padding: 0,
    margin: 0,
    overflow: 'hidden',
  },
  logo: {
    width: '100%',
    height: 270,
    marginBottom: 0,
    resizeMode: 'cover',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    marginTop: 4,
    color: 'black',
  },
  formContainer: {
    width: '100%',
    paddingHorizontal: 20,
  },
  input: {
    marginBottom: 8,
    backgroundColor: '#fff',
    borderColor: '#ccc',
    color: '#000',
  },
  errorText: {
    color: '#e74c3c',
    fontSize: 12,
    marginLeft: 8,
    marginTop: -4,
    marginBottom: 8,
    fontWeight: 'bold',
  },
  button: {
    marginTop: 16,
    height: 50,
    justifyContent: 'center',
    backgroundColor: 'rgb(125, 76, 236)',
    borderRadius: 8,
    elevation: 3,
  },
  registerContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
  },
  registerText: {
    fontSize: 16,
    color: 'black',
    },
  menuButtonContainer: {
    marginTop: 24,
    alignItems: 'center',
  },
  menuButton: {
    width: '100%',
    backgroundColor: 'rgb(125, 76, 236)',
    borderWidth: 1.5,
  },
});

export default LoginScreen;
